import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Tab, Tabs } from '@mui/material';

import { EMPLOYEE_DIRECTORY_PERMISSION } from '@/features/employees';
import { satisfiesPermission, usePermissions } from '@/shared/permissions';
import type { PermissionRequirement } from '@/shared/permissions';
import { PageContainer, useDocumentTitle } from '@/shared/ui';

interface UsersTab {
  /** Path segment under `/team`. The empty string is the index tab. */
  segment: string;
  labelKey: string;
  /**
   * Omitted for the self-service tab — everyone may inspect their own access. An array means any one
   * of the codes suffices, mirroring the route guard in `routes.tsx`.
   */
  permission?: PermissionRequirement;
}

const TABS: UsersTab[] = [
  // The directory is readable at `employees.view`; the three administration tabs below are not
  // (plan-auth-001 Phase 3.2). A view-only member therefore lands on a two-tab workspace: the staff
  // list and their own access.
  { segment: '', labelKey: 'workspace.tabs.users', permission: EMPLOYEE_DIRECTORY_PERMISSION },
  { segment: 'groups', labelKey: 'workspace.tabs.groups', permission: 'employees.manage' },
  { segment: 'access', labelKey: 'workspace.tabs.access', permission: 'employees.manage' },
  { segment: 'review', labelKey: 'workspace.tabs.review', permission: 'employees.manage' },
  { segment: 'my-access', labelKey: 'workspace.tabs.myAccess' },
];

/**
 * The Users workspace shell.
 *
 * <p>Users, groups and access were three unrelated ideas before this screen existed — the portal had one
 * flat employees table and nothing else. They are one job (deciding who may do what), so they share one
 * workspace and one page header instead of three sidebar entries competing for the same mental slot.
 *
 * <p>The section is called <b>Users</b> and not "Company". It was the latter briefly, which collided with
 * Settings → Company — the tenant's legal details — so the portal had two unrelated destinations wearing
 * the same word. This one is the people; that one is the company's own registration data. Only the label
 * moved: the URL stays `/team` so existing links and bookmarks keep working.
 *
 * <p>The "My access" tab carries no permission: reading your own resolved access is self-service, and an
 * ordinary staff member who cannot see a button needs somewhere to find out why.
 */
export function UsersLayout() {
  const { t } = useTranslation('access');
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useDocumentTitle(t('workspace.title'));

  const visible = TABS.filter(
    (tab) => !tab.permission || satisfiesPermission(tab.permission, hasPermission),
  );

  // Longest match wins so `/team/my-access` does not resolve to the index tab.
  const active =
    visible
      .filter((tab) => (tab.segment ? pathname.startsWith(`/team/${tab.segment}`) : true))
      .sort((a, b) => b.segment.length - a.segment.length)[0] ?? visible[0];

  return (
    <PageContainer title={t('workspace.title')} subtitle={t('workspace.subtitle')} maxWidth={1440}>
      <Tabs
        value={active?.segment ?? ''}
        onChange={(_, segment: string) => navigate(segment ? `/team/${segment}` : '/team')}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {visible.map((tab) => (
          <Tab key={tab.segment} value={tab.segment} label={t(tab.labelKey)} />
        ))}
      </Tabs>
      <Outlet />
    </PageContainer>
  );
}
